import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { type Command, useCommandStore } from "@/lib/command-registry";
import { formatShortcut } from "@/lib/shortcuts";
import Fuse from "fuse.js";
/**
 * Command palette (mod+k). Walks the command registry, fuzzy-matches with
 * Fuse.js, and runs the chosen command. Stage 1 ships the chrome; later
 * stages register navigation actions ("Go to workspace", "Open project X").
 */
import { useEffect, useMemo, useState } from "react";

function groupBy<T, K extends string>(items: T[], key: (item: T) => K | undefined): Map<K, T[]> {
  const out = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    if (!k) continue;
    const bucket = out.get(k);
    if (bucket) bucket.push(item);
    else out.set(k, [item]);
  }
  return out;
}

export function CommandPalette(): JSX.Element | null {
  const open = useCommandStore((s) => s.paletteOpen);
  const closePalette = useCommandStore((s) => s.closePalette);
  const openPalette = useCommandStore((s) => s.openPalette);
  const togglePalette = useCommandStore((s) => s.togglePalette);
  const openShortcuts = useCommandStore((s) => s.openShortcuts);
  const commandsMap = useCommandStore((s) => s.commands);

  // App-level defaults. Other commands are registered by their owning components.
  const defaultRegister = useCommandStore((s) => s.register);
  useEffect(() => {
    const unregisterPalette = defaultRegister({
      id: "app.open-palette",
      title: "Show command palette",
      group: "Application",
      shortcut: ["mod", "k"],
      keywords: ["search", "command", "find"],
      perform: () => useCommandStore.getState().openPalette(),
    });
    const unregisterShortcuts = defaultRegister({
      id: "app.open-shortcuts",
      title: "Show keyboard shortcuts",
      group: "Application",
      shortcut: ["shift", "/"],
      keywords: ["help", "shortcuts", "keys"],
      perform: () => useCommandStore.getState().openShortcuts(),
    });
    return () => {
      unregisterPalette();
      unregisterShortcuts();
    };
  }, [defaultRegister]);

  // Global mod+k / shift+/ binding. The dialog itself traps focus.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        togglePalette();
        return;
      }
      if (e.shiftKey && (e.key === "?" || e.key === "/")) {
        e.preventDefault();
        openShortcuts();
        return;
      }
      if (e.key === "/" && !mod && !e.shiftKey && !e.altKey) {
        const target = e.target as HTMLElement | null;
        if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
        if (target?.isContentEditable) return;
        e.preventDefault();
        openPalette();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openPalette, openShortcuts, togglePalette]);

  const [query, setQuery] = useState("");
  const all = useMemo(() => Array.from(commandsMap.values()), [commandsMap]);
  const fuse = useMemo(
    () =>
      new Fuse(all, {
        keys: ["title", "keywords", "group"],
        threshold: 0.4,
        ignoreLocation: true,
      }),
    [all]
  );
  const results = useMemo<Command[]>(() => {
    if (!query.trim()) return all;
    return fuse.search(query).map((r) => r.item);
  }, [all, fuse, query]);

  const grouped = useMemo(() => groupBy(results, (c) => c.group ?? "Other"), [results]);

  function runCommand(cmd: Command): void {
    closePalette();
    void cmd.perform();
  }

  return (
    <CommandDialog open={open} onOpenChange={(o) => (o ? openPalette() : closePalette())}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Type a command or search…"
      />
      <CommandList>
        <CommandEmpty>No commands found.</CommandEmpty>
        {Array.from(grouped.entries()).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map((cmd) => (
              <CommandItem
                key={cmd.id}
                value={`${cmd.title} ${cmd.group ?? ""} ${(cmd.keywords ?? []).join(" ")}`}
                onSelect={() => runCommand(cmd)}
              >
                <span>{cmd.title}</span>
                {cmd.shortcut ? (
                  <CommandShortcut>{formatShortcut(cmd.shortcut)}</CommandShortcut>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
